import * as React from 'react';
import {
    Box,
    Button,
    Dialog,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import pxToRem from 'assets/theme/function/px-to-rem';
import borders from 'assets/theme/base/borders';
import colors from 'assets/theme/base/colors';
import { ProductResponse } from 'types/product/product';
import { RowDataType, TableMetaData } from 'types/common/common';
import { useEffect, useState } from 'react';
import { openConfirmationDialogs } from 'store/slices/confirmation-dialog/confirmation-dialog-slice';
import { StyledTableRow } from 'component/tabel-pagination-style';
import { ReactComponent as Edit } from 'assets/images/staff-user/edit.svg';
import { ReactComponent as Delete } from 'assets/images/staff-user/delete.svg';
import { useAppDispatch } from 'hooks/redux';
import {
    useLazyPriceListQuery,
    usePriceDeleteMutation,
} from 'store/slices/product/product-price';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import CreateProductPrice from 'pages/product/product-detail-page/product-price/add-product-price';
import EditProductPrice from 'pages/product/product-detail-page/product-price/edit-product-price';
import { PriceInfo } from 'types/product/product-price';
import { ReactComponent as Plus } from 'assets/images/plus.svg';
import dayjs from 'dayjs';
import rgba from 'assets/theme/function/rgba';

const defaultTableStaffDataList: TableMetaData[] = [
    {
        key: 'price',
        position: 1,
        title: 'Price',
        isSortable: false,
    },
    {
        key: 'discountPercent',
        position: 2,
        title: 'Discount Percent',
        isSortable: false,
    },
    {
        key: 'paymentMode',
        position: 3,
        title: 'Payment Mode',
        isSortable: false,
    },
    {
        key: 'currency',
        position: 4,
        title: 'Currency',
        isSortable: false,
    },
    {
        key: 'action',
        position: 5,
        title: 'Action',
        isSortable: false,
    },
];
interface Props {
    currencyList?: string[];
    dataProductId?: string;
    productId?: string;
    productData: ProductResponse | undefined;
}

const Index: React.FC<Props> = (props: Props) => {
    const { currencyList, productId, dataProductId } = props;
    const [tableMetaDataList] = useState<TableMetaData[]>(
        defaultTableStaffDataList,
    );
    const [productPriceId, setProductPriceId] = React.useState<string>('');
    const [openDialogAddProductPrice, setOpenDialogAddProductPrice] =
        React.useState(false);
    const [openDialogEditProductPrice, setOpenDialogEditProductPrice] =
        React.useState(false);
    const [tableData, setTableData] = useState<RowDataType[]>([]);

    const dispatch = useAppDispatch();

    const [userPriceList, { data }] = useLazyPriceListQuery();

    const [
        priceDeleteApi,
        {
            error: deleteError,
            isError: deleteIsError,
            isSuccess: deleteIsSuccess,
        },
    ] = usePriceDeleteMutation();

    const getPriceList = () => {
        userPriceList(
            productId ? productId : dataProductId ? dataProductId : '',
        );
    };

    const createNewPrice = () => {
        setOpenDialogAddProductPrice(true);
    };

    const deletePrice = (id: PriceInfo) => {
        priceDeleteApi(id.id);
    };
    const handleClickEditPrice = (id: string) => {
        setOpenDialogEditProductPrice(true);
        setProductPriceId(id);
    };

    useEffect(() => {
        const tableData: RowDataType[] = data
            ? data.map((fullInfo) => {
                  return {
                      ...fullInfo,
                      action: (
                          <Box
                              display={'flex'}
                              justifyContent={'center'}
                              gap={2}
                          >
                              <Tooltip title="Delete">
                                  <Delete
                                      cursor="pointer"
                                      onClick={() => {
                                          dispatch(
                                              openConfirmationDialogs({
                                                  textInfo:
                                                      'Are You Sure Want To Delete Card',
                                                  handleConfirm: deletePrice,
                                                  parameters: {
                                                      id: fullInfo.id,
                                                  },
                                              }),
                                          );
                                      }}
                                  />
                              </Tooltip>
                              <Tooltip title="Edit">
                                  <Edit
                                      cursor="pointer"
                                      onClick={() =>
                                          handleClickEditPrice(fullInfo.id)
                                      }
                                  />
                              </Tooltip>
                          </Box>
                      ),
                      rowComponent: StyledTableRow,
                  };
              })
            : [];
        setTableData(tableData);
    }, [data]);

    useEffect(() => {
        if (deleteIsSuccess || productId) {
            setOpenDialogAddProductPrice(false);
            getPriceList();
        }
    }, [deleteIsSuccess, productId]);

    useEffect(() => {
        const error = deleteError;

        if (error && 'data' in error && error.data?.message) {
            dispatch(
                openSnackbar({
                    message: error.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [deleteIsError]);

    return (
        <>
            <Box
                bgcolor={colors.secondary.light}
                borderRadius={borders.borderRadius.md}
                mt={pxToRem(15)}
            >
                <Box
                    display={'flex'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    p={1}
                >
                    <Typography variant="h6" pl={pxToRem(10)}>
                        Product Price
                    </Typography>

                    <Button onClick={createNewPrice} size={'small'}>
                        <Box
                            p={pxToRem(5)}
                            display={'flex'}
                            alignItems={'center'}
                        >
                            <Plus />
                            <Typography
                                pl={pxToRem(10)}
                                color={rgba(colors.primary.main, 1)}
                                variant={'subtitle1'}
                            >
                                Create New Price
                            </Typography>
                        </Box>
                    </Button>
                </Box>

                <TableContainer
                    sx={{
                        minHeight: pxToRem(150),
                        maxHeight: pxToRem(300),
                        overflowY: 'auto',
                    }}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                {tableMetaDataList.map((col) => (
                                    <TableCell key={col.key}>
                                        {col.title}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableData.map((row, idx) => (
                                <StyledTableRow key={idx} data={row}>
                                    {tableMetaDataList.map((col) => (
                                        <TableCell key={col.key}>
                                            {col.key === 'createdDate' &&
                                            row[col.key]
                                                ? dayjs(row[col.key]).format(
                                                      'DD.MM.YYYY',
                                                  )
                                                : row[col.key]}
                                        </TableCell>
                                    ))}
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <Dialog
                open={openDialogAddProductPrice}
                onClose={() => setOpenDialogAddProductPrice(false)}
            >
                <CreateProductPrice
                    dataProductId={dataProductId}
                    productId={productId}
                    currencyList={currencyList}
                    getPriceList={getPriceList}
                    setOpenDialogAddProductPrice={setOpenDialogAddProductPrice}
                />
            </Dialog>

            {productPriceId && (
                <Dialog
                    open={openDialogEditProductPrice}
                    onClose={() => setOpenDialogEditProductPrice(false)}
                >
                    <EditProductPrice
                        productId={productId}
                        currencyList={currencyList}
                        productPriceId={productPriceId}
                        setOpenDialogEditProductPrice={
                            setOpenDialogEditProductPrice
                        }
                        getPriceList={getPriceList}
                    />
                </Dialog>
            )}
        </>
    );
};

export default Index;
